'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Button } from '@/shared/ui/shadcn/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/shadcn/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/shared/ui/shadcn/dropdown-menu';
import { EstimateExportMenuItems } from '@/features/design/estimate';
import { toast } from '@/shared/lib/toast';
import { useRenderDownload } from '@/features/design/render/hooks/use-render-download';
import { shareRenderImage } from '@/features/design/render/lib/share-render';
import { useConceptActionsDialog } from '../hooks/use-concept-actions-dialog';
import {
  Trash2,
  AlertTriangle,
  MoreVertical,
  Download,
  Replace,
  Info,
  Pencil,
  Bookmark,
  Share2,
  Link as LinkIcon,
  Heart,
  Search,
  Camera,
} from 'lucide-react';
import { cn } from '@/shared/lib/cn';

interface ConceptActionsProps {
  // Common props
  projectId: string;
  conceptId: string;
  conceptName: string;
  /** project_sequence_number from API. Used as the prefix in shared/saved
   *  filenames (e.g. `concept-5-abc123.jpg`) so files are recognisable
   *  on the user's device. Optional — falls back to `render-…` when absent. */
  conceptSequenceNumber?: number;
  renderFileUrl?: string | null;
  renderId?: string | null;
  isUpscaled?: boolean;

  // Mode:
  //  - 'page'         — concept-page kebab (optional favorite toggle /
  //                     Edit angles / Delete concept). Mirrors Apple's
  //                     `ConceptMenuContent`.
  //  - 'editor'       — angle-level menu on the main image (when
  //                     isDerivative=false) or on a derivative card
  //                     (isDerivative=true). Mirrors Apple's `AngleMenuContent`.
  //  - 'concept-card' — compact menu used on concept thumbnails.
  mode?: 'concept-card' | 'editor' | 'page';

  // Concept-card mode props (matching Android HistoryItemCard menu)
  isFavorite?: boolean;
  isMainAngle?: boolean;
  onToggleFavorite?: () => void;
  onViewDetails?: () => void;

  // Page mode: when true the kebab shows a favorite toggle as its first item
  // (single-column, where the standalone FavoriteButton is hidden). In
  // two-column the favorite lives as a dedicated header button and this is
  // false. The toggle reuses `isFavorite` + `onToggleFavorite`.
  showFavorite?: boolean;

  // Editor mode props
  isDerivative?: boolean;
  hasRender?: boolean;
  onStartNewPhoto?: () => void;
  onReplacePhoto?: () => void;
  onRenameAngle?: () => void;
  onDeleteConcept?: () => void;
  onDeleteAngle?: () => void;
  onDeleteRender?: (renderId: string) => void;
  /** Page mode, Estimate tab only — set when the concept has a deletable
   *  (ready/failed) estimate. Adds a destructive "Delete estimate" item with
   *  its own confirmation dialog. Mirrors Apple/Android. */
  onDeleteEstimate?: () => Promise<unknown> | void;
  isDeletingEstimate?: boolean;
  /** Page mode, Estimate tab only — set when the concept's current estimate
   *  is `ready`. Adds an "Export estimate" submenu (PDF / Excel / Text).
   *  Mirrors Apple's concept-page kebab. */
  exportableEstimateId?: string;
  onEditAngles?: () => void;
  onSaveAsReference?: () => void;
  isSavingAsReference?: boolean;
  /** Opens RenderInfoSheet for the current render. Only renders the menu
   *  item when both this callback is set and a render exists. */
  onShowRenderInfo?: () => void;

  // Optional styling
  disabled?: boolean;
  className?: string;
}

export function ConceptActions({
  projectId,
  conceptId,
  conceptName,
  conceptSequenceNumber,
  renderFileUrl,
  renderId,
  isUpscaled = false,
  mode = 'concept-card',
  isFavorite = false,
  isMainAngle = true,
  onToggleFavorite,
  onViewDetails,
  showFavorite = false,
  isDerivative = false,
  hasRender = false,
  onStartNewPhoto,
  onReplacePhoto,
  onEditAngles,
  onRenameAngle,
  onDeleteConcept,
  onDeleteAngle,
  onDeleteRender,
  onDeleteEstimate,
  isDeletingEstimate = false,
  exportableEstimateId,
  onSaveAsReference,
  isSavingAsReference = false,
  onShowRenderInfo,
  disabled = false,
  className = '',
}: ConceptActionsProps) {
  const tRender = useTranslations('render.options');
  const tConcept = useTranslations('concept');
  const tConceptDelete = useTranslations('concept.delete');
  const tConceptActions = useTranslations('conceptActions');
  const tHistory = useTranslations('history');
  const tCommon = useTranslations('common');
  const tDeleteRender = useTranslations('deleteRender');
  const tAngleMenu = useTranslations('angle.menu');
  const tRenderInfo = useTranslations('render.info');
  const tEstimateDelete = useTranslations('common.estimate.delete');
  const tEstimateExport = useTranslations('common.estimate.export');

  const [menuOpen, setMenuOpen] = useState(false);
  const [showDeleteEstimateModal, setShowDeleteEstimateModal] = useState(false);

  // Download and upscale logic
  const {
    download,
    download4K,
    isUpscaling,
    upscaleProgress,
    hasUpscale,
  } = useRenderDownload({
    renderId,
    renderFileUrl,
    isUpscaled,
    conceptSequenceNumber,
  });

  // Dialog state management
  const {
    showDeleteConceptModal,
    showDeleteAngleModal,
    showDeleteRenderModal,
    openDeleteConceptModal,
    openDeleteAngleModal,
    openDeleteRenderModal,
    setShowDeleteConceptModal,
    setShowDeleteAngleModal,
    setShowDeleteRenderModal,
    handleDeleteConcept,
    handleDeleteAngle,
    handleDeleteRender,
    isDeleting,
  } = useConceptActionsDialog({
    projectId,
    conceptId,
    renderId,
    onDeleteConcept,
    onDeleteAngle,
    onDeleteRender,
  });

  // Close menu before download
  const handleDownload = async () => {
    setMenuOpen(false);
    await download();
  };

  const handleUpscale = async () => {
    setMenuOpen(false);
    await download4K();
  };

  const handleSaveAsReference = () => {
    setMenuOpen(false);
    onSaveAsReference?.();
  };

  const handleDeleteEstimate = async () => {
    try {
      await onDeleteEstimate?.();
    } catch {
      // Error toast is raised by the delete mutation — just close the dialog.
    } finally {
      setShowDeleteEstimateModal(false);
    }
  };

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          {/* Single button: 44px hit area, visible 28px circle painted via
              `before:` pseudo-element so taps land anywhere within the
              outer 44px frame instead of just the visible pill. Visible
              background colour comes from the caller via `className`
              (parsed by `before:bg-*` arbitrary classes there). */}
          <Button
            variant="ghost"
            size="icon"
            disabled={disabled}
            className={cn(
              'relative inline-flex h-11 w-11 items-center justify-center bg-transparent hover:bg-transparent',
              "before:absolute before:inset-2 before:rounded-full before:content-['']",
              className
            )}
          >
            <MoreVertical className="relative h-4 w-4" />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          {/* Information — opens RenderInfoSheet for the current render. */}
          {mode !== 'page' && onShowRenderInfo && renderId && (
            <>
              <DropdownMenuItem
                onClick={() => {
                  setMenuOpen(false);
                  onShowRenderInfo();
                }}
                className="cursor-pointer"
              >
                <Info className="h-4 w-4 mr-2" />
                {tRenderInfo('title')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}

          {/* Download options (available when render exists) */}
          {mode !== 'page' && renderId && renderFileUrl && (
            <>
              <DropdownMenuItem
                onClick={handleDownload}
                disabled={isUpscaling}
                className="cursor-pointer"
              >
                <Download className="h-4 w-4 mr-2" />
                <span>
                  {tRender('downloadRender')}
                  {false && /* UPSCALE_HIDDEN */ isUpscaled ? ' (4K)' : ''}
                </span>
              </DropdownMenuItem>

              {false &&
                /* UPSCALE_HIDDEN */ hasUpscale &&
                !isUpscaled && (
                  <DropdownMenuItem
                    onClick={handleUpscale}
                    disabled={isUpscaling}
                    className="cursor-pointer"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    <span className="flex-1">{tRender('generate4K')}</span>
                    {isUpscaling ? (
                      <span className="text-list-item-meta text-muted-foreground ml-auto">
                        {upscaleProgress > 0
                          ? `${Math.round(upscaleProgress)}%`
                          : tRender('upscaling')}
                      </span>
                    ) : (
                      <span className="text-list-item-meta text-muted-foreground ml-auto">
                        {tRender('upscaleLabel')}
                      </span>
                    )}
                  </DropdownMenuItem>
                )}

              <DropdownMenuItem
                onClick={() => {
                  setMenuOpen(false);
                  if (!renderFileUrl) return;
                  // Pass the actual image file when the browser supports it so the
                  // mobile share sheet (Chrome Android / Safari iOS) shows the
                  // rendering preview + Save Image option, matching the native
                  // apps. Desktop browsers transparently fall back to URL share
                  // and finally to clipboard copy.
                  const shortId = (renderId ?? renderFileUrl).slice(-6);
                  const filename = conceptSequenceNumber != null
                    ? `concept-${conceptSequenceNumber}-${shortId}.jpg`
                    : `render-${shortId}.jpg`;
                  void shareRenderImage({
                    url: renderFileUrl,
                    filename,
                    title: conceptName,
                    onClipboardFallback: () => {
                      toast.success(tRender('linkCopied'));
                    },
                  });
                }}
                className="cursor-pointer"
              >
                <Share2 className="h-4 w-4 mr-2" />
                {tRender('shareRender')}
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => {
                  setMenuOpen(false);
                  if (renderFileUrl) {
                    navigator.clipboard.writeText(renderFileUrl).then(() => {
                      toast.success(tRender('linkCopied'));
                    });
                  }
                }}
                className="cursor-pointer"
              >
                <LinkIcon className="h-4 w-4 mr-2" />
                {tRender('copyUrl')}
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={handleSaveAsReference}
                disabled={isSavingAsReference}
                className="cursor-pointer"
              >
                <Bookmark className="h-4 w-4 mr-2" />
                {tRender('saveAsReference')}
              </DropdownMenuItem>

              <DropdownMenuSeparator />
            </>
          )}

          {/* Page-level menu — mirrors Apple ConceptMenuContent. */}
          {mode === 'page' ? (
            <>
              {/* Favorite toggle — only in single-column, where the standalone
                  FavoriteButton is hidden. Reuses the same toggle handler so
                  the debounced mutation isn't duplicated. */}
              {showFavorite && onToggleFavorite && (
                <>
                  <DropdownMenuItem
                    onClick={() => {
                      setMenuOpen(false);
                      onToggleFavorite();
                    }}
                    className="cursor-pointer"
                  >
                    <Heart
                      className={cn(
                        'mr-2 h-4 w-4',
                        isFavorite && 'fill-primary text-primary'
                      )}
                    />
                    {isFavorite
                      ? tConceptActions('unfavorite')
                      : tConceptActions('favorite')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              {/* Edit views — Views (Design) tab only (callback presence
                  carries the condition, mirrors Apple/Android). */}
              {onEditAngles && (
                <DropdownMenuItem
                  onClick={() => {
                    setMenuOpen(false);
                    onEditAngles();
                  }}
                  className="cursor-pointer"
                >
                  <Camera className="mr-2 h-4 w-4" />
                  {tConcept('menu.editAngles')}
                </DropdownMenuItem>
              )}
              {/* Export estimate — Estimate tab only, when the current
                  estimate is ready (prop presence carries the condition).
                  PDF / Excel / Text items shared with the estimate card
                  export menu. */}
              {exportableEstimateId && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Download className="mr-2 h-4 w-4" />
                    {tEstimateExport('menuItem')}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <EstimateExportMenuItems
                      estimateId={exportableEstimateId}
                    />
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              )}
              {/* No leading separator when every non-destructive item is
                  hidden (e.g. Album tab in two-column, where favorite is a
                  header button) — the favorite block above already carries
                  its own trailing separator. */}
              {(onEditAngles || exportableEstimateId) && (
                <DropdownMenuSeparator />
              )}
              {/* Delete estimate — Estimate tab only, when a ready/failed
                  estimate exists (callback presence carries the condition). */}
              {onDeleteEstimate && (
                <DropdownMenuItem
                  onClick={() => {
                    setMenuOpen(false);
                    setShowDeleteEstimateModal(true);
                  }}
                  className="text-destructive focus:text-destructive cursor-pointer"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {tEstimateDelete('menuItem')}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={openDeleteConceptModal}
                className="text-destructive focus:text-destructive cursor-pointer"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {tConcept('menu.deleteConcept')}
              </DropdownMenuItem>
            </>
          ) : mode === 'editor' && isDerivative ? (
            <>
              {onRenameAngle && (
                <DropdownMenuItem
                  onClick={() => {
                    setMenuOpen(false);
                    onRenameAngle();
                  }}
                  className="cursor-pointer"
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  {tAngleMenu('rename')}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={onReplacePhoto}
                className="cursor-pointer"
              >
                <Replace className="h-4 w-4 mr-2" />
                {tRender('replaceSourcePhoto')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {/* Apple order: Delete render → divider → Delete angle. */}
              {renderId && (
                <DropdownMenuItem
                  onClick={openDeleteRenderModal}
                  className="text-destructive cursor-pointer focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {tRender('deleteSelectedRender')}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={openDeleteAngleModal}
                className="text-destructive cursor-pointer focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {tRender('deleteThisAngle')}
              </DropdownMenuItem>
            </>
          ) : mode === 'editor' && !isDerivative ? (
            <>
              {onRenameAngle && (
                <DropdownMenuItem
                  onClick={() => {
                    setMenuOpen(false);
                    onRenameAngle();
                  }}
                  className="cursor-pointer"
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  {tAngleMenu('rename')}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={hasRender ? onStartNewPhoto : onReplacePhoto}
                className="cursor-pointer"
              >
                <Replace className="h-4 w-4 mr-2" />
                {tRender('replaceSourcePhoto')}
              </DropdownMenuItem>
              {renderId && onDeleteRender && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={openDeleteRenderModal}
                    className="text-destructive cursor-pointer focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {tRender('deleteSelectedRender')}
                  </DropdownMenuItem>
                </>
              )}
            </>
          ) : (
            /* Concept card mode — matching Android HistoryItemCard menu */
            <>
              {/* Toggle favorite */}
              {onToggleFavorite && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setMenuOpen(false);
                    onToggleFavorite();
                  }}
                  className="cursor-pointer"
                >
                  <Heart
                    className={cn(
                      'mr-2 h-4 w-4',
                      isFavorite && 'fill-primary text-primary'
                    )}
                  />
                  {isFavorite
                    ? tConceptActions('unfavorite')
                    : tConceptActions('favorite')}
                </DropdownMenuItem>
              )}

              {/* View details */}
              {renderId && onViewDetails && (
                <DropdownMenuItem
                  onClick={() => {
                    setMenuOpen(false);
                    onViewDetails();
                  }}
                  className="cursor-pointer"
                >
                  <Search className="mr-2 h-4 w-4" />
                  {tHistory('viewDetails')}
                </DropdownMenuItem>
              )}

              <DropdownMenuSeparator />

              {/* Delete — different text for main angle vs secondary */}
              <DropdownMenuItem
                onClick={
                  isMainAngle ? openDeleteConceptModal : openDeleteRenderModal
                }
                className="text-destructive focus:text-destructive cursor-pointer"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {isMainAngle
                  ? tConcept('menu.deleteConcept')
                  : tDeleteRender('title')}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Delete Concept confirmation dialog */}
      <AlertDialog
        open={showDeleteConceptModal}
        onOpenChange={setShowDeleteConceptModal}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <AlertDialogTitle>
                {tConceptDelete('dialogTitle')}
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="space-y-2">
              <span>
                {tConceptDelete('dialogDescription', { name: conceptName })}
              </span>
              <span className="block text-form-help">
                {tConceptDelete('dialogWarning')}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {tConceptDelete('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConcept}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting
                ? tConceptDelete('deleting')
                : tConceptDelete('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Estimate confirmation dialog */}
      <AlertDialog
        open={showDeleteEstimateModal}
        onOpenChange={setShowDeleteEstimateModal}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <AlertDialogTitle>
                {tEstimateDelete('dialogTitle')}
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription>
              {tEstimateDelete('dialogDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingEstimate}>
              {tEstimateDelete('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteEstimate}
              disabled={isDeletingEstimate}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeletingEstimate
                ? tEstimateDelete('deleting')
                : tEstimateDelete('confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete view (secondary angle) confirmation dialog. Gated because the
          view-delete fans out across the shared view set and has NO undo
          (unlike render/concept deletes, which stay immediate + undo toast).
          On confirm the existing delete mutation runs; a 409 cross-concept
          conflict surfaces afterwards via AngleDeleteConflictDialog. */}
      <AlertDialog
        open={showDeleteAngleModal}
        onOpenChange={setShowDeleteAngleModal}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <AlertDialogTitle>
                {tRender('deleteAngleConfirmTitle')}
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription>
              {tRender('deleteAngleConfirmMessage')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAngle}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {tCommon('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Render confirmation dialog */}
      <AlertDialog
        open={showDeleteRenderModal}
        onOpenChange={setShowDeleteRenderModal}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tDeleteRender('title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {tDeleteRender('description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteRender}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {tCommon('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
